/**
 * Client-side encryption utilities for sensitive fields (SSN, etc)
 * Uses AES-256 encryption with a derived key from user auth
 */

import CryptoJS from "crypto-js";

/**
 * Generate an encryption key from auth token + device fingerprint
 * This ensures each user/device has a unique key
 */
export function generateEncryptionKey(authToken: string): string {
  // In production, combine with device fingerprint
  // For now, use a derived key from the auth token
  return CryptoJS.SHA256(authToken).toString();
}

/**
 * Encrypt sensitive data (SSN, etc)
 */
export function encryptField(
  plaintext: string,
  encryptionKey: string
): string {
  try {
    const encrypted = CryptoJS.AES.encrypt(plaintext, encryptionKey).toString();
    return encrypted;
  } catch (error) {
    console.error("Encryption failed:", error);
    throw new Error("Failed to encrypt sensitive data");
  }
}

/**
 * Decrypt sensitive data
 */
export function decryptField(ciphertext: string, encryptionKey: string): string {
  try {
    const decrypted = CryptoJS.AES.decrypt(ciphertext, encryptionKey).toString(
      CryptoJS.enc.Utf8
    );
    return decrypted;
  } catch (error) {
    console.error("Decryption failed:", error);
    throw new Error("Failed to decrypt sensitive data");
  }
}

/**
 * Encrypt an entire form submission object
 * Only encrypts fields marked as encrypted
 */
export function encryptFormData(
  data: Record<string, any>,
  encryptedFieldIds: string[],
  encryptionKey: string
): Record<string, any> {
  const encrypted = { ...data };

  for (const fieldId of encryptedFieldIds) {
    if (data[fieldId]) {
      encrypted[fieldId] = encryptField(data[fieldId].toString(), encryptionKey);
    }
  }

  return encrypted;
}

/**
 * Decrypt sensitive fields in form data
 */
export function decryptFormData(
  data: Record<string, any>,
  encryptedFieldIds: string[],
  encryptionKey: string
): Record<string, any> {
  const decrypted = { ...data };

  for (const fieldId of encryptedFieldIds) {
    if (data[fieldId]) {
      try {
        decrypted[fieldId] = decryptField(
          data[fieldId],
          encryptionKey
        );
      } catch {
        // If decryption fails, leave the field as is
        decrypted[fieldId] = data[fieldId];
      }
    }
  }

  return decrypted;
}

/**
 * Mask sensitive data for display (e.g., XXX-XX-1234 for SSN)
 */
export function maskSSN(ssn: string): string {
  const clean = ssn.replace(/\D/g, "");
  if (clean.length !== 9) return ssn;
  return `XXX-XX-${clean.slice(-4)}`;
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split("@");
  const masked =
    local.charAt(0) + "*".repeat(local.length - 2) + local.charAt(local.length - 1);
  return `${masked}@${domain}`;
}
